// **Ein Schritt als Karte** — derselbe Baustein im Wizard (views/wizard) und
// in den Formular-Reitern der Detailseite (views/detail/form-tab.tsx).
//
// Der Zustand entsteht einmal aus dem Mandanten (`fromTenant`); wer ihn neu
// haben will (nach „Speichern" auf der Detailseite), hängt die Karte über
// `key` neu ein. Den Fuß (Speichern, Weiter, Übernehmen) bestimmt der
// Aufrufer — er bekommt `collect()`, das den Rumpf aus dem aktuellen Zustand
// baut.

import { useState, type ReactNode } from "react";
import type { Catalog, HostCapacity, StepCheck, Tenant } from "../../api/types";
import { Card } from "../../ui/card";
import { authFromTenant, authToBody } from "./auth";
import { AuthForm } from "./auth-form";
import { customerFromTenant, customerToBody } from "./customer";
import { CustomerForm } from "./customer-form";
import { firstAdminFromTenant, firstAdminToBody } from "./first-admin";
import { FirstAdminForm } from "./first-admin-form";
import { integrationsFromTenant, integrationsToBody } from "./integrations";
import { IntegrationsForm } from "./integrations-form";
import { licenseFromTenant, licenseToBody } from "./license";
import { LicenseForm } from "./license-form";
import { modelsFromTenant, modelsToBody } from "./models";
import { ModelsForm } from "./models-form";
import type { StepBody, StepDef } from "./types";

export type StepNumber = 1 | 2 | 3 | 4 | 5 | 6;

export const customerStep = { fromTenant: customerFromTenant, toBody: customerToBody, Form: CustomerForm } satisfies StepDef<ReturnType<typeof customerFromTenant>, ReturnType<typeof customerToBody>, Tenant | null>;
export const licenseStep = { fromTenant: licenseFromTenant, toBody: licenseToBody, Form: LicenseForm } satisfies StepDef<ReturnType<typeof licenseFromTenant>, ReturnType<typeof licenseToBody>>;
export const authStep = { fromTenant: authFromTenant, toBody: authToBody, Form: AuthForm } satisfies StepDef<ReturnType<typeof authFromTenant>, ReturnType<typeof authToBody>>;
export const modelsStep = { fromTenant: modelsFromTenant, toBody: modelsToBody, Form: ModelsForm } satisfies StepDef<ReturnType<typeof modelsFromTenant>, ReturnType<typeof modelsToBody>>;
export const integrationsStep = { fromTenant: integrationsFromTenant, toBody: integrationsToBody, Form: IntegrationsForm } satisfies StepDef<ReturnType<typeof integrationsFromTenant>, ReturnType<typeof integrationsToBody>>;
export const firstAdminStep = { fromTenant: firstAdminFromTenant, toBody: firstAdminToBody, Form: FirstAdminForm } satisfies StepDef<ReturnType<typeof firstAdminFromTenant>, ReturnType<typeof firstAdminToBody>>;

export interface StepEditorProps {
  step: StepNumber;
  tenant: Tenant | null;
  catalog: Catalog;
  check: StepCheck | null;
  host?: HostCapacity | null;
  title: ReactNode;
  hint?: ReactNode;
  /** Speichert einen Rumpf; `true`, wenn der Server ihn angenommen hat. */
  onSave: (body: StepBody) => Promise<boolean>;
  foot: (collect: () => StepBody) => ReactNode;
}

type HostProps = Omit<StepEditorProps, "step" | "tenant">;

export function StepEditor({ step, tenant, ...rest }: StepEditorProps) {
  if (step === 1) return <StepHost def={customerStep} tenant={tenant} {...rest} />;
  // Ab Schritt 2 gibt es immer einen Entwurf — ohne ihn kein Formular.
  if (!tenant) return null;
  switch (step) {
    case 2: return <StepHost def={licenseStep} tenant={tenant} {...rest} />;
    case 3: return <StepHost def={authStep} tenant={tenant} {...rest} />;
    case 4: return <StepHost def={modelsStep} tenant={tenant} {...rest} />;
    case 5: return <StepHost def={integrationsStep} tenant={tenant} {...rest} />;
    case 6: return <StepHost def={firstAdminStep} tenant={tenant} {...rest} />;
  }
}

function StepHost<S, B extends StepBody, T extends Tenant | null>({ def, tenant, catalog, check, host, title, hint, onSave, foot }: HostProps & {
  def: StepDef<S, B, T>;
  tenant: T;
}) {
  const [state, setState] = useState<S>(() => def.fromTenant(tenant, catalog));
  const collect = (): StepBody => def.toBody(state, catalog);
  const Form = def.Form;
  return (
    <Card title={title} hint={hint} foot={foot(collect)}>
      <form noValidate onSubmit={(e) => e.preventDefault()}>
        <Form tenant={tenant} catalog={catalog} state={state} setState={setState} check={check} host={host} save={() => onSave(collect())} />
      </form>
    </Card>
  );
}
